// Image-based similarity for the comparison site.
//
// The site compares two PNG exports from the extension's "Download PNG"
// button. The PNG is 1080×1440 (4:3 height:width): the top 360px strip
// carries a QR code (encoding version + time range) and human-readable text;
// the bottom 1080×1080 is the KDE-blurred inferno heatmap on a white #ffffff
// background. Only the bottom 1080×1080 plot area is used for similarity — the
// strip is cropped out before comparison.
//
// The primary metric is cosine similarity of per-pixel Rec.709 luminance,
// with background pixels masked out so that two users with different topics
// but similar-sized empty regions don't get an inflated score from their
// shared white area.
//
// Pure helpers (luminance, masking, cosine) are exported for unit tests;
// `loadImageToImageData` is the only browser-dependent function.

import decodeQR from "qr/decode.js";
import {
    decodeMetadata,
    PNG_HEIGHT,
    PNG_WIDTH,
    PLOT_SIZE,
    type PngMetadata,
} from "../core/qr-metadata.ts";

/** Width of the full exported PNG. */
export const EXPECTED_WIDTH = PNG_WIDTH; // 1080
/** Height of the full exported PNG. */
export const EXPECTED_HEIGHT = PNG_HEIGHT; // 1440
/** Size of the square plot area used for similarity comparison. */
export const PLOT_AREA_SIZE = PLOT_SIZE; // 1080

/** Background detection threshold. The export fills with #ffffff (255,255,255)
 * and then draws the inferno heatmap on top. Inferno's brightest color is
 * [252, 255, 164] (B=164), and even blended toward white the B channel of any
 * data pixel stays well below 250 — so this threshold cleanly separates
 * background from data with a large safety margin. */
const BG_THRESHOLD = 250;

/** Rec.709 luminance — the perceptually-relevant single number per pixel.
 * Monotonic in density under the inferno palette, so luminance similarity
 * ≈ "do the bright spots overlap". */
export function luminance(r: number, g: number, b: number): number {
    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** True if the pixel is background (the white fill, no heatmap drawn here). */
export function isBackground(r: number, g: number, b: number): boolean {
    return r >= BG_THRESHOLD && g >= BG_THRESHOLD && b >= BG_THRESHOLD;
}

/** Structural shape accepted by luminanceMasked — ImageData satisfies this,
 * but tests can pass a plain object without needing the ImageData constructor
 * (which is a DOM API not available in bun:test). */
export interface PixelGrid {
    data: Uint8ClampedArray | number[];
    width: number;
    height: number;
}

/**
 * Build a luminance vector from a pixel grid, with background pixels set to 0.
 * The result is a Float32Array of length width*height, where each entry is
 * the Rec.709 luminance of the corresponding pixel if it's a data pixel, or 0
 * if it's background. This is the vector the cosine similarity operates on.
 *
 * Masking the background to 0 means: (1) two empty regions don't contribute
 * to the dot product (so users with different topics but similar-sized empty
 * areas aren't scored as similar for that reason), and (2) a data pixel in
 * image A overlapping a background pixel in image B contributes 0 to the dot
 * product (correctly — that's a region where A has data and B doesn't). */
export function luminanceMasked(image: PixelGrid): Float32Array {
    const out = new Float32Array(image.width * image.height);
    const d = image.data;
    for (let i = 0, j = 0; i < d.length; i += 4, j++) {
        const r = d[i]!,
            g = d[i + 1]!,
            b = d[i + 2]!;
        if (isBackground(r, g, b)) continue; // stays 0
        out[j] = luminance(r, g, b);
    }
    return out;
}

/** Cosine similarity of two non-negative vectors. In [0, 1] for non-negative
 * inputs; 1 = identical direction (same pattern, possibly different scale),
 * 0 = no overlap (disjoint non-zero regions). Scale-invariant: a heavy user
 * with 10x more visits but the same topic pattern as a light user scores 1.0.
 *
 * Throws on length mismatch — callers must ensure both images are the same
 * size (the site enforces EXPECTED_WIDTH/EXPECTED_HEIGHT). */
export function cosineSimilarity(
    a: ArrayLike<number>,
    b: ArrayLike<number>,
): number {
    if (a.length !== b.length) {
        throw new Error(`length mismatch: ${a.length} vs ${b.length}`);
    }
    let dot = 0,
        na = 0,
        nb = 0;
    for (let i = 0; i < a.length; i++) {
        const av = a[i]!,
            bv = b[i]!;
        dot += av * bv;
        na += av * av;
        nb += bv * bv;
    }
    if (na === 0 || nb === 0) return 0; // one side has no data
    return dot / (Math.sqrt(na) * Math.sqrt(nb));
}

/** Coverage: fraction of pixels that are non-background (have data). Useful
 * for showing "your heatmap covers X% of the topic space" alongside the
 * similarity number. */
export function coverage(masked: ArrayLike<number>): number {
    let n = 0;
    for (let i = 0; i < masked.length; i++) if (masked[i]! > 0) n++;
    return n / masked.length;
}

export interface ImageComparison {
    /** Primary headline: cosine similarity of masked luminance vectors, [0,1]. */
    cosine: number;
    /** Fraction of pixels with data in image A. */
    coverageA: number;
    /** Fraction of pixels with data in image B. */
    coverageB: number;
    /** Fraction of pixels with data in both A and B (overlap). */
    overlap: number;
}

/** Compare two masked luminance vectors. Either side being all-zero (empty
 * image) yields cosine 0 — there's nothing to compare. */
export function compareImages(
    a: ArrayLike<number>,
    b: ArrayLike<number>,
): ImageComparison {
    if (a.length !== b.length) {
        throw new Error(`image size mismatch: ${a.length} vs ${b.length}`);
    }
    let both = 0,
        na = 0,
        nb = 0;
    for (let i = 0; i < a.length; i++) {
        const av = a[i]! > 0,
            bv = b[i]! > 0;
        if (av) na++;
        if (bv) nb++;
        if (av && bv) both++;
    }
    return {
        cosine: cosineSimilarity(a, b),
        coverageA: na / a.length,
        coverageB: nb / b.length,
        overlap: both / a.length,
    };
}

// --- Browser-only (not exercised by unit tests) ---

/** Load an image File and extract the plot-area ImageData (the bottom
 * PLOT_AREA_SIZE × PLOT_AREA_SIZE pixels). Also decodes the QR code from the
 * metadata strip at the top. Throws if the QR code is missing or unreadable.
 *
 * Accepts PNG or JPEG, any size — as long as the aspect ratio is 3:4
 * (width:height, e.g. 1080×1440, 810×1080, 270×360).  The image is scaled to
 * EXPECTED_WIDTH × EXPECTED_HEIGHT on a canvas before processing. */
export async function loadImageToImageData(
    file: File,
): Promise<{ imageData: ImageData; metadata: PngMetadata }> {
    const url = URL.createObjectURL(file);
    try {
        const img = await new Promise<HTMLImageElement>((resolve, reject) => {
            const i = new Image();
            i.onload = () => resolve(i);
            i.onerror = () => reject(new Error("could not decode image"));
            i.src = url;
        });

        // Verify 3:4 (width:height) aspect ratio within 1 % tolerance.
        const expectedRatio = PNG_WIDTH / PNG_HEIGHT;
        const actualRatio = img.width / img.height;
        if (Math.abs(actualRatio - expectedRatio) > 0.01) {
            throw new Error(
                `image must be 3:4 aspect ratio (e.g. ${PNG_WIDTH}×${PNG_HEIGHT}), ` +
                    `got ${img.width}×${img.height}`,
            );
        }

        // Scale to the expected pixel dimensions.
        const canvas = document.createElement("canvas");
        canvas.width = EXPECTED_WIDTH;
        canvas.height = EXPECTED_HEIGHT;
        const ctx = canvas.getContext("2d")!;
        ctx.drawImage(img, 0, 0, EXPECTED_WIDTH, EXPECTED_HEIGHT);
        const fullData = ctx.getImageData(
            0,
            0,
            EXPECTED_WIDTH,
            EXPECTED_HEIGHT,
        );

        // Decode QR from the metadata strip (top part of the PNG).
        const metadata = decodeQrFromStrip(fullData);
        if (!metadata) {
            throw new Error("QR code not found in the image");
        }

        // Extract just the plot area (bottom PLOT_AREA_SIZE × PLOT_AREA_SIZE).
        const stripH = EXPECTED_HEIGHT - PLOT_AREA_SIZE;
        const plotData = ctx.getImageData(
            0,
            stripH,
            PLOT_AREA_SIZE,
            PLOT_AREA_SIZE,
        );
        return { imageData: plotData, metadata };
    } finally {
        URL.revokeObjectURL(url);
    }
}

/** Scan the metadata strip (top STRIP_HEIGHT rows) for a QR code and decode
 * it as heatmap metadata. Returns null if no valid QR is found. */
function decodeQrFromStrip(fullData: ImageData): PngMetadata | null {
    const stripH = EXPECTED_HEIGHT - PLOT_SIZE;
    // Extract the strip as a separate ImageData for decodeQR.
    const stripCanvas = document.createElement("canvas");
    stripCanvas.width = EXPECTED_WIDTH;
    stripCanvas.height = stripH;
    const stripCtx = stripCanvas.getContext("2d")!;
    stripCtx.putImageData(fullData, 0, 0);
    const stripData = stripCtx.getImageData(0, 0, EXPECTED_WIDTH, stripH);
    try {
        const decoded = decodeQR({
            data: stripData.data,
            width: stripData.width,
            height: stripData.height,
        });
        return decodeMetadata(decoded);
    } catch {
        return null;
    }
}
